/**
 * ProPage: dedicated landing page for fitting professionals and
 * green-grass pro shops, rebuilt on the premium `.mkt` marketing design
 * system (marketingRedesign.css) that the golfer HomePage uses. Full
 * dark takeover: the page renders its own sticky nav and footer, so the
 * global Layout chrome is suppressed for `/pro` in Layout.tsx.
 *
 * Lead positioning: the fitting-to-cash workflow plus the loss-prevention
 * shield, with the 2-month free trial as the conversion hook. Structured
 * as a single narrative that any pro can read top to bottom, with the
 * #workflow and #shield anchors letting a visitor jump to their audience.
 *
 * PRESERVED FROM PRIOR VERSION (do not modify):
 *   - useRedirectIfPwaLaunch('/pro/admin') PWA-launch bounce
 *   - useDocumentTitle + useHeadMeta with all three JSON-LD payloads
 *   - PricingMatrix with PRO_PRICING_TIERS / PRO_PRICING_ROWS
 *   - Every content constant (WORKFLOW_PILLARS, LEAD_GEN_POINTS,
 *     FINANCIAL_SHIELD_CARDS, PRO_FEATURE_GROUPS, FAQ_ITEMS)
 *
 * ADDED 2026-09-18 (feature-coverage sweep): the page was missing
 * several shipped capabilities and said almost nothing about what the
 * golfer on the other side of the counter gets, which is the pitch a
 * head pro takes to a committee. Two acts added, both as NEW constants
 * so the preserved ones above stay untouched:
 *   - RETENTION_TOOLS  -> "Between the fittings" act (pro-golfer
 *     messaging, Demo Fleet, AI fitting briefings, customer invites)
 *   - GOLFER_BENEFITS  -> "What your golfers get" act (#golfers)
 * Plus two pricing rows (messaging, demo fleet) and two FAQ entries.
 * Keep this page, apps/fitting/public/pro.md and the pricing matrix in
 * sync: pro.md feeds /llms-full.txt, which is what AI crawlers and
 * outbound tooling read instead of the rendered page.
 *
 * Routes preserved:
 *   "Start your 2-month free trial"  → /pro/register?source=pro-page
 *   Final CTA                        → /pro/register?source=pro-page-footer
 *   Pricing CTAs                     → /pro/register?plan=…&source=pro-pricing
 *   "Sign in"                        → /pro/login
 *   Demo                             → /contact?subject=pro-demo
 *
 * Copy constraint: no em dashes, en dashes, or spaced-hyphen clause
 * joiners in visible copy. It should read as written by a person.
 */

import { useState } from 'react';
import {
  ArrowRight,
  BellRing,
  ChevronDown,
  ClipboardList,
  CreditCard,
  FileCheck,
  Lock,
  MessageCircle,
  Package,
  PackageCheck,
  PackagePlus,
  QrCode,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Store,
  Truck,
  UserPlus,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useHeadMeta } from '../hooks/useHeadMeta';
import { useRedirectIfPwaLaunch } from '../hooks/useRedirectIfPwaLaunch';
import { PricingMatrix, type PricingTier, type PricingRow } from '@golfauth/ui';
import MarketingField from '../components/marketing/MarketingField';
import MarketingNav from '../components/marketing/MarketingNav';
import '../styles/marketingRedesign.css';

const NAVY = '#00112B';
const FREE_TRIAL_CTA = '/pro/register?source=pro-page';
const FINAL_CTA = '/pro/register?source=pro-page-footer';

// ─────────────────────────────────────────────────────────────────────
// The Fitter's Workflow: five pillars that form the fitting-to-cash
// spine. Written for a master fitter audience: specific tooling
// vocabulary (loft, lie, frequency, MOI), outcomes-focused (revenue per
// fitting, time saved on admin), confident not precious.
// ─────────────────────────────────────────────────────────────────────
const WORKFLOW_PILLARS = [
  {
    icon: Users,
    eyebrow: 'The customer record',
    title: 'Every fitting starts with the bag they walked in with.',
    body:
      'Handicap, swing speed range, ball flight tendencies, lie-angle preference, every prior fitting note, every club currently in their bag. Pull it up before the launch monitor warms up. When they come back in 18 months for a wedge refit, you remember exactly what you tried with the driver.',
  },
  {
    icon: ClipboardList,
    eyebrow: 'The build spec',
    title: 'Spec drift dies here.',
    body:
      'Every shaft, grip, loft, lie, swing weight, length, and frequency, captured against the configuration. Multi-option quotes when you fit more than one shaft brand. The order packet to your suppliers writes itself; supplier dispatch generates from accepted quotes.',
  },
  {
    icon: CreditCard,
    eyebrow: 'The payment',
    title: 'No invoicing. No 30-day chase.',
    body:
      'Customer accepts the quote in their inbox, pays online via Stripe Connect, payout lands in your bank on Stripe\'s normal schedule. No invoicing, no 30-day chase, no awkward "just following up" email. PayTo for Australian customers, card for everyone else.',
  },
  {
    icon: PackagePlus,
    eyebrow: 'The purchase order',
    title: 'The order packet writes itself.',
    body:
      'Turn an accepted quote into a purchase order in a tap. Split a build across multiple suppliers or send a single-supplier order, emailed straight to your rep or manufacturer with every shaft, grip, and spec already filled in. No retyping line items into three different portals.',
  },
  {
    icon: PackageCheck,
    eyebrow: 'The handoff',
    title: 'Delivered, and provenance-complete.',
    body:
      'Track every build from ordered to fulfilled in Order Management. When it reaches the golfer, shipped to them or picked up from your bench after final adjustments, it lands in their GolfAuth bag as a Pro-Verified club: full spec, your shop on the record, and a chain of custody that carries forward every time they sell or transfer it.',
  },
];

// ─────────────────────────────────────────────────────────────────────
// The Lead Gen Engine: the consumer-app-discoverability story. Being on
// GolfAuth Pro makes you visible to every golfer in your postcode using
// the consumer app.
// ─────────────────────────────────────────────────────────────────────
const LEAD_GEN_POINTS = [
  {
    icon: ShieldCheck,
    title: 'Verified Pro profile',
    body:
      'Your shop, your specialties (driver, irons, putter, wedges), your real fitting history. Every Pro-Verified build you ship strengthens your network credibility, and surfaces you ahead of generic listings.',
  },
  {
    icon: Users,
    title: 'Local discovery',
    body:
      'Golfers searching "Find a Professional" in your region see you first. Your postcode plus specialty tags drive the ranking; your existing customer base reinforces it.',
  },
  {
    icon: FileCheck,
    title: 'Direct booking',
    body:
      'Your existing scheduling link is embedded on your profile. Golfer goes from "I need a fitting" to your calendar in three taps. No quote forms, no inbox tennis.',
  },
  {
    icon: Truck,
    title: 'Trust signals that compound',
    body:
      'Receipt-Verified and Pro-Verified clubs in your customers\' bags carry your shop\'s name forward. Three years from now, every driver you fit is still searchable as your work.',
  },
  {
    icon: Store,
    title: 'Sell to local golfers',
    body:
      'List demo units, trade-ins, and shop stock to GolfAuth golfers near you in the Pro Shop marketplace. They pay by card at checkout with verified pickup, and you keep the sale minus a flat 1% plus tax. A local resale channel running on the same verified identities as everything else.',
  },
];

// ─────────────────────────────────────────────────────────────────────
// The Pro Shop Financial Shield: three loss-prevention cards for
// green-grass shops (member clubs, resort courses, public daily-fee).
// The Pro Basic-tier narrative: not a lite version of the fitter
// platform, a different product entirely.
// ─────────────────────────────────────────────────────────────────────
const FINANCIAL_SHIELD_CARDS = [
  {
    icon: ShieldCheck,
    eyebrow: 'Trade-in verification',
    title: 'Verification before store credit is issued.',
    body:
      'Serial scan against the GolfAuth verification network: registered ownership, receipt-verified provenance, duplicate-serial flags. Plus chain-of-custody transfers: members hand off pre-registered clubs digitally so your inventory inherits their full lineage. Counterfeit Scotty Camerons get refused at the counter, not discovered after $400 in credit has already been issued.',
  },
  {
    icon: Lock,
    eyebrow: 'L&F audit trail',
    title: 'No proof of ownership, no item released.',
    body:
      'Staff batch-scan found items during quiet shifts. Claimants must show their GolfAuth registration or upload original-receipt proof before any high-value item leaves the counter. Every release logged: who, when, what proof, which staff. Insurance carriers like structure. You like not eating a $500 rangefinder when someone else collects it on the honor system.',
  },
  {
    icon: Users,
    eyebrow: 'Repeat-guest tracking',
    title: '500 local members, with their gear history attached.',
    body:
      'Every member who has traded in, lost a club, or registered gear at your shop sits in your customer database with their full history attached. When the same member walks back in six months later asking about a wedge swap, your assistant on shift has the same context you do. Institutional memory in spite of staff rotation.',
  },
];

// ─────────────────────────────────────────────────────────────────────
// Between the fittings: the retention surfaces. A fitting is one
// afternoon; these are what keep the shop in the golfer's pocket until
// the next build. All four ship today. Messaging is ungated (the
// Messages tab in AdminDashboard carries no `requires`), Demo Fleet and
// AI briefings are Standard+ per src/lib/featureAccess.ts.
// ─────────────────────────────────────────────────────────────────────
const RETENTION_TOOLS = [
  {
    icon: MessageCircle,
    eyebrow: 'Messaging',
    title: 'They can message you straight from the app.',
    body:
      'Two-way threads between your shop and any golfer linked to you, with read receipts and typing indicators. A question about stock, a follow-up on a build, a nudge about a refit before the season. It sits next to the customer record instead of on somebody\u2019s personal phone. Every tier.',
  },
  {
    icon: Package,
    eyebrow: 'Demo fleet',
    title: 'Loaners that actually come back.',
    body:
      'Check a demo out against its serial and the customer who took it, set the due date, log the condition it left in. Overdue loans flag in the tab and a nightly email tells you what is still out. Standard and Premium.',
  },
  {
    icon: Sparkles,
    eyebrow: 'AI fitting briefing',
    title: 'Briefed before they walk in.',
    body:
      'The briefing reads the bag they already own, finds the gaps in it, and suggests a demo sequence before the session starts. You open the bay knowing what you want in their hands first. 20 a month on Standard, unlimited on Premium.',
  },
  {
    icon: UserPlus,
    eyebrow: 'Customer invites',
    title: 'Bring the book you already have.',
    body:
      'Invite your existing customers across. The invitation carries your name, they arrive already linked to your shop, and their fitting history starts on day one instead of on their next visit.',
  },
];

// ─────────────────────────────────────────────────────────────────────
// What your golfers get: the consumer half of the network, written for
// the pro who has to justify GolfAuth to a committee or a membership.
// Everything here works on a free golfer account. The paid golfer tiers
// (caddie chat, valuations, bag reports) are called out in the act
// footnote so the page never implies a member has to pay for the
// shop-side benefits.
// ─────────────────────────────────────────────────────────────────────
const GOLFER_BENEFITS = [
  {
    icon: Smartphone,
    eyebrow: 'The bag in their pocket',
    title: 'Every club they own, registered and valued.',
    body:
      'Your members can photograph the bag before they walk in, so the fitting briefing reads the clubs they actually carry. The account holds spec, serial, purchase record, and current market value. Free to download, free to hold all 14. You are the reason they have it.',
  },
  {
    icon: ShieldCheck,
    eyebrow: 'Pro-Verified',
    title: 'The build you handed over, documented for life.',
    body:
      'It lands in their bag as a Pro-Verified club: full spec, your shop named on it, and a chain of custody that carries forward every time that club is sold, transferred, insured or claimed. Only a verified professional can issue that record, and it stays on the club for life.',
  },
  {
    icon: ClipboardList,
    eyebrow: 'Their own portal',
    title: 'Quotes and orders they can actually find.',
    body:
      'They accept the quote, pay, and follow the build from their own GolfAuth login. Past fittings, saved configurations, live orders, all in one place instead of buried in an email thread from March.',
  },
  {
    icon: QrCode,
    eyebrow: 'Collection',
    title: 'Pickups that prove themselves.',
    body:
      'Pro Shop orders are collected against a signed QR code at your counter. The right club goes to the right person, the handover is logged, and neither side has to take the other on trust.',
  },
  {
    icon: BellRing,
    eyebrow: 'Lost & Found',
    title: 'Told the moment their club turns up.',
    body:
      'Scan a found club into Lost & Found and the registered owner is emailed automatically. Your member hears from the shop before they have finished searching the boot of the car. That is the story they tell in the clubhouse.',
  },
];

// ─────────────────────────────────────────────────────────────────────
// Feature showcase groups. Unifies the three feature narratives
// (workflow, lead-gen, financial shield) into one grouped showcase that
// mirrors the golfer HomePage treatment. Reuses the arrays above as the
// per-group card sets so the copy stays in one place. The #workflow and
// #shield ids are consumed by the audience-selector jump links above, so
// they must stay on their group headers.
// ─────────────────────────────────────────────────────────────────────
type ProFeatureCard = { icon: typeof Users; eyebrow?: string; title: string; body: string };
type ProFeatureGroup = {
  id?: string;
  eyebrow: string;
  title: string;
  description?: string;
  priceLine?: string;
  footnote?: string;
  image?: string;
  cards: ProFeatureCard[];
};

const PRO_FEATURE_GROUPS: ProFeatureGroup[] = [
  {
    id: 'workflow',
    eyebrow: 'Run every fitting',
    title: 'Built around how you actually fit.',
    image: '/marketing/pro-workflow.jpg',
    description:
      'Five pillars, one spine: the customer record, the build spec, the payment, the purchase order, the handoff. Each one replaces a folder of email threads, a tab of spreadsheet rows, or a stack of paper receipts.',
    cards: WORKFLOW_PILLARS,
  },
  {
    eyebrow: 'Between the fittings',
    title: 'The months between builds are where shops lose people.',
    image: '/marketing/network-grid.jpg',
    description:
      'A fitting is one afternoon. The relationship is every month after it. Messaging, demo loans, and a briefing that reads their bag before they arrive: the surfaces that keep you in the conversation until the next build.',
    cards: RETENTION_TOOLS,
  },
  {
    eyebrow: 'Get discovered',
    title: 'Your shop, in the pocket of every golfer in your postcode.',
    image: '/marketing/pro-discovery.jpg',
    description:
      'The GolfAuth consumer app sends golfers looking for a fitting straight to verified pros, with your real outcomes attached. Every Pro-Verified build you ship now is a search hit later.',
    footnote:
      'Listed alphabetically inside your postcode. Specialty matches rank above generalists. Customer outcomes drive the long-tail credibility. There is no pay-to-play tier on the consumer side.',
    cards: LEAD_GEN_POINTS,
  },
  {
    id: 'shield',
    eyebrow: 'Protect your margin',
    title: 'For green-grass shops protecting their margin.',
    image: '/marketing/pro-shield.jpg',
    description:
      'Pro Basic is not a lite version of the fitter platform. It is a different product: counterfeit trade-in verification, a lost & found audit trail, and a 500-member directory that survives staff rotation.',
    priceLine: '$19.99/mo. One caught fake or one avoided wrong-person release pays the year.',
    footnote:
      'Honest caveat: the chain-of-custody play works strongest on members who already use GolfAuth. For members who do not, the serial verification still runs against the network\'s receipt-verified records and duplicate-serial flags, a structural upgrade over visual inspection at the counter. Coverage compounds as the network grows.',
    cards: FINANCIAL_SHIELD_CARDS,
  },
  {
    id: 'golfers',
    eyebrow: 'What your golfers get',
    title: 'The other half of this runs in their pocket.',
    image: '/marketing/feat-caddie.jpg',
    description:
      'GolfAuth Pro is one half of a network. The other half is the golfer app your members and customers get free. Run your shop on GolfAuth and every golfer walks out with something that keeps working after they leave the car park, with your shop attached to it.',
    footnote:
      'Everything on this list works on a free golfer account. The paid golfer tiers (AI caddie chat, bag valuations, bag reports) start at $1.99 a month with a two-month free trial, and nothing your shop does is gated behind a member paying for one.',
    cards: GOLFER_BENEFITS,
  },
];

// ─────────────────────────────────────────────────────────────────────
// Pricing matrix: pro tiers (D9 pricing v2, June 2026).
// Caps must stay in sync with apps/fitting/src/lib/proTier.ts.
// ─────────────────────────────────────────────────────────────────────
const PRO_PRICING_TIERS: PricingTier[] = [
  {
    name: 'Basic',
    tagline: 'Built for green-grass shops protecting margin.',
    monthlyPrice: '$19.99',
    annualPrice: '$199.99',
    annualSavings: 'Save 17%',
    ctaLabel: 'Start 2-month free trial',
    ctaHref: '/pro/register?plan=basic&source=pro-pricing',
    ctaVariant: 'secondary',
  },
  {
    name: 'Standard',
    tagline: 'Fitting workflow + Pro Shop marketplace.',
    monthlyPrice: '$49.99',
    annualPrice: '$499.99',
    annualSavings: 'Save 17%',
    highlight: true,
    badge: 'Most popular',
    ctaLabel: 'Start 2-month free trial',
    ctaHref: '/pro/register?plan=standard&source=pro-pricing',
  },
  {
    name: 'Premium',
    tagline: 'Unlimited ops for high-volume shops.',
    monthlyPrice: '$99.99',
    annualPrice: '$999.99',
    annualSavings: 'Save 17%',
    ctaLabel: 'Start 2-month free trial',
    ctaHref: '/pro/register?plan=premium&source=pro-pricing',
  },
];

const PRO_PRICING_ROWS: PricingRow[] = [
  { label: 'Customer base', isSection: true },
  {
    label: 'Customer database',
    detail: 'Handicap, swing speed, ball flight, full fitting history.',
    values: ['500 customers', '2,500 customers', 'Unlimited'],
  },
  {
    label: 'Fitting sessions / month',
    values: ['5', '20', 'Unlimited'],
  },
  {
    label: 'Quote-to-customer / month',
    values: ['10', 'Unlimited', 'Unlimited'],
  },
  {
    label: 'Customer messaging',
    detail: 'Two-way threads with linked golfers, read receipts, typing indicators.',
    values: [true, true, true],
  },
  { label: 'Lost & Found reporting', values: [true, true, true] },
  {
    label: 'Demo fleet',
    detail: 'Loaner checkout by serial, due dates, condition notes, overdue email.',
    values: [false, true, true],
  },

  { label: 'Quotes & payments', isSection: true },
  { label: 'Quote creation + email delivery', values: [true, true, true] },
  {
    label: 'Multi-option quotes',
    detail: 'Offer multiple shafts / grips per quote.',
    values: [false, false, true],
  },
  {
    label: 'Online customer checkout',
    detail: 'Customer accepts your quote and pays in-app, no invoicing back-and-forth.',
    values: [false, true, true],
  },
  {
    label: 'Stripe Connect (accept payments)',
    detail: 'Card payouts land in your bank on Stripe\'s normal schedule.',
    values: [false, true, true],
  },
  { label: 'Order management dashboard', values: [false, true, true] },
  {
    label: 'Automated quote follow-ups',
    detail: '3-touch email sequence after a quote is sent.',
    values: [false, false, true],
  },
  { label: 'Promotion vouchers (Stripe coupons)', values: [false, true, true] },

  { label: 'Pro Shop marketplace', isSection: true },
  {
    label: 'Sell inventory to customers',
    detail:
      'List demo / trade-in / shop stock to linked GolfAuth customers with verified pickup.',
    values: [false, true, true],
  },
  {
    label: 'Connect platform fee',
    detail:
      'GolfAuth share on Stripe Connect checkouts (fitting quotes and Pro Shop marketplace sales). Golfer-to-golfer marketplace sales are not charged this fee.',
    values: ['N/A', '1% + tax', '1% + tax'],
  },

  { label: 'AI & fitting tools', isSection: true },
  {
    label: 'AI fitting briefings / month',
    detail: 'Pre-session bag analysis + suggested demo sequence.',
    values: [false, '20', 'Unlimited'],
  },
  { label: 'Receipt template uploads', values: [true, true, true] },
  { label: 'Custom receipt templates', values: [false, true, true] },

  { label: 'Supply chain', isSection: true },
  {
    label: 'Purchase orders',
    values: ['N/A', 'Single supplier', 'Multi-supplier'],
  },

  { label: 'Insights & support', isSection: true },
  { label: 'Revenue dashboard', values: [false, true, true] },
  { label: 'Equipment trend insights', values: [false, false, true] },
  {
    label: 'Recovery analytics + CSV export',
    values: [false, false, true],
  },
  { label: 'Phone support', values: [false, false, true] },
];

const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  {
    q: 'How is GolfAuth Pro different from spreadsheets or generic CRMs?',
    a: 'Generic CRMs don’t understand fitting. GolfAuth Pro is built specifically for the fitting workflow: capturing club configurations, launch monitor sessions, supplier orders, and Stripe Connect payments. Every customer interaction is captured in a structured way that’s useful for the next fitting, not just the current one.',
  },
  {
    q: 'How does the Stripe Connect payment flow work?',
    a: 'You connect your Stripe account once during onboarding. When customers pay through GolfAuth checkout (fitting quotes or Pro Shop marketplace), funds go to your Stripe account on Stripe’s normal payout schedule. GolfAuth takes a platform fee of 1% of the sale price plus applicable tax on that fee (e.g. GST in Australia), shown where relevant before you publish or collect. Stripe’s card processing fee still applies separately.',
  },
  {
    q: 'What is the 1% + tax Connect fee?',
    a: 'On Stripe Connect checkouts (fitting quotes and Pro Shop marketplace sales), GolfAuth takes 1% of the sale price plus tax on that fee (for example in Australia that is 10% GST on the 1% fee, about 1.1% all-in). Marketplace listing prices are entered excluding tax; Stripe Tax adds the buyer’s tax at checkout. Golfer-to-golfer marketplace sales are not charged this Pro Connect platform fee.',
  },
  {
    q: 'Can I use my existing supplier relationships?',
    a: 'Yes. Your suppliers are managed in your private supplier database. When a customer accepts a quote, GolfAuth generates a supplier purchase order with their preferred format and delivery details. Your supplier relationships stay yours. GolfAuth just makes the order administration faster.',
  },
  {
    q: 'What happens to my customer data if I leave GolfAuth Pro?',
    a: 'Your data is yours. You can export your full customer database, fitting history, quote records, and supplier information at any time. We provide CSV export by default and JSON export for technical users.',
  },
  {
    q: 'Do my customers need their own GolfAuth accounts?',
    a: 'It helps but isn’t strictly required for basic fitting. Customers who have GolfAuth accounts get the Pro-Verified digital records of clubs you build for them, which strengthens their relationship with you. We help you invite them through a co-branded onboarding flow.',
  },
  {
    q: 'Does GolfAuth integrate with my accounting software?',
    a: 'Xero integration is on the roadmap for Q3 2026, with MYOB and QuickBooks following. In the meantime, GolfAuth Pro provides exportable reports compatible with standard accounting workflows.',
  },
  {
    q: 'How does the 2-month free trial work?',
    a: 'Sign up with your business details. No credit card required. You get full access to GolfAuth Pro for 2 months: all features, no limits. At the end of the trial, you can choose Basic, Standard, or Premium pricing. If GolfAuth Pro isn’t right for you, do nothing and your account simply expires.',
  },
  {
    q: 'What do my golfers get out of it?',
    a: 'A GolfAuth account that holds their whole bag, free. Every club you build for them is recorded as a Pro-Verified club with your shop named on it and a chain of custody that follows the club for life. They accept quotes, pay, and track orders in their own portal, message your shop directly, collect Pro Shop orders against a signed QR code, and get an automatic email the moment you scan one of their clubs into Lost & Found. The paid golfer tiers start at $1.99 a month and add AI caddie chat, bag valuations, and bag reports, but none of the shop-side benefits require a golfer to pay for anything.',
  },
  {
    q: 'Can I message my customers inside GolfAuth?',
    a: 'Yes, on every tier including Basic. Any golfer linked to your shop can start a thread with you, and you can start one with them, with read receipts and typing indicators. The thread sits alongside the customer record, so the conversation about a build stays attached to the build. It replaces the personal-mobile-number problem most shops end up with.',
  },
  {
    q: 'Can I have multiple fitters under one business account?',
    a: 'Yes. Pro tier supports multiple fitters under a single business account, with role-based permissions, individual customer attribution, and consolidated reporting. Basic is single-fitter.',
  },
];

const FOUNDING_APPLY_PATH = '/pro/founding-fitters/apply';

function FoundingShopsBanner() {
  return (
    <p
      className="reveal mono"
      style={{
        marginTop: '20px',
        fontSize: '.78rem',
        letterSpacing: '.04em',
        color: 'var(--muted)',
        lineHeight: 1.6,
        maxWidth: '62ch',
      }}
    >
      <Link to={FOUNDING_APPLY_PATH} style={{ color: 'var(--cyan)' }}>
        Founding
      </Link>{' '}
      shops: discovery ranks inside your postcode — the first verified shop in
      yours holds the top spot.
    </p>
  );
}

export default function ProPage() {
  // PWA-launch redirect: if the user installed the Pro PWA from /pro
  // (iOS pre-16.4 ignores manifest start_url and uses the install URL),
  // bounce them to /admin so the ProtectedRoute → /pro/login chain
  // fires. No-op for normal browser sessions.
  useRedirectIfPwaLaunch('/pro/admin');
  useDocumentTitle('GolfAuth Pro | Quote sent at 11am. Paid by lunch.');
  useHeadMeta({
    description:
      'Fitting workflow for master fitters, loss-prevention for green-grass pro shops. Customer records, multi-option quotes, Stripe Connect checkout, supplier POs, customer messaging, demo fleet. Your golfers get a free verified bag. 2 months free, no card.',
    canonical: 'https://golfauth.com/pro',
    og: {
      title: 'GolfAuth Pro — Quote sent at 11am. Paid by lunch.',
      description:
        'For master fitters: customer records, multi-option quotes, Stripe Connect, supplier dispatch. For pro shops: counterfeit verification and audit trails.',
      type: 'website',
      url: 'https://golfauth.com/pro',
      image: 'https://golfauth.com/og/pro.png',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'GolfAuth Pro — Quote sent at 11am. Paid by lunch.',
      description:
        'Fitting workflow for master fitters, loss-prevention shield for green-grass pro shops. 2-month free trial.',
      image: 'https://golfauth.com/og/pro.png',
    },
    // AEO Phase 1: three structured-data payloads on the Pro page:
    //   1. Organization: same canonical entity as HomePage (referenced
    //      by @id so search engines de-dupe correctly).
    //   2. Service: GolfAuth Pro, with all three tiers exposed as
    //      Offer + PriceSpecification entries. Drives "best pro shop
    //      software" / "best CRM for golf fitters" style citations.
    //   3. FAQPage: Q&A facts for AI extraction.
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': 'https://golfauth.com#organization',
        name: 'GolfAuth',
        url: 'https://golfauth.com',
        logo: 'https://golfauth.com/logos/golfauth-light-1280w.webp',
        description:
          'WITB — every club, known and yours. GolfAuth Pro is the professional platform for fitting businesses and green-grass pro shops.',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': 'https://golfauth.com/pro#golfauth-pro-service',
        name: 'GolfAuth Pro',
        description:
          'The professional platform for golf fitting businesses, PGA professionals, and green-grass pro shops. Includes customer records, fitting sessions, multi-option quotes, Stripe Connect payments (1% + tax platform fee), Pro Shop marketplace, supplier dispatch, AI fitting briefings, two-way customer messaging, demo and loaner fleet management, counterfeit trade-in verification, and Lost & Found audit trails. Golfers fitted by a GolfAuth Pro get a free verified digital bag, Pro-Verified club records naming the shop that built them, a quote and order portal, direct messaging with the shop, QR-verified collection, and automatic notification when a club of theirs is scanned into Lost & Found.',
        provider: { '@id': 'https://golfauth.com#organization' },
        serviceType:
          'Golf fitting business platform, pro shop operations software, marketplace, and counterfeit verification',
        audience: [
          { '@type': 'Audience', audienceType: 'Master club fitters' },
          { '@type': 'Audience', audienceType: 'PGA professionals' },
          { '@type': 'Audience', audienceType: 'Green-grass pro shops' },
          { '@type': 'Audience', audienceType: 'Country clubs' },
          { '@type': 'Audience', audienceType: 'Resort golf courses' },
        ],
        areaServed: { '@type': 'Place', name: 'Worldwide' },
        offers: [
          {
            '@type': 'Offer',
            name: 'Pro Basic',
            description:
              'Built for green-grass pro shops protecting margin. Counterfeit trade-in verification, Lost & Found audit trail, 500-customer directory, customer database with full gear history, two-way customer messaging, 5 fittings and 10 quotes per month. Two-month free trial.',
            price: '199.99',
            priceCurrency: 'AUD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '19.99',
              priceCurrency: 'AUD',
              billingDuration: 'P1M',
              unitText: 'MONTH',
            },
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Pro Standard',
            description:
              'For working master fitters. Stripe Connect checkout (1% + tax platform fee on fitting quotes and marketplace sales), Pro Shop marketplace, 20 fittings and 20 AI fitting briefings per month, unlimited quotes, single-supplier purchase orders, order management, demo and loaner fleet tracking, two-way customer messaging, promotion vouchers, custom receipt templates. Two-month free trial.',
            price: '499.99',
            priceCurrency: 'AUD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '49.99',
              priceCurrency: 'AUD',
              billingDuration: 'P1M',
              unitText: 'MONTH',
            },
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: 'Pro Premium',
            description:
              'For the top of the market. Unlimited everything plus Stripe Connect (1% + tax platform fee on fitting quotes and marketplace sales), Pro Shop marketplace, multi-option shaft and grip quotes, multi-supplier purchase orders, automated quote follow-ups, demo and loaner fleet tracking, two-way customer messaging, equipment trend insights, recovery analytics CSV export, phone support. Two-month free trial.',
            price: '999.99',
            priceCurrency: 'AUD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '99.99',
              priceCurrency: 'AUD',
              billingDuration: 'P1M',
              unitText: 'MONTH',
            },
            availability: 'https://schema.org/InStock',
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="mkt mkt--pro" style={{ backgroundColor: NAVY }}>
      {/* Ambient topographic canvas + scroll-reveal driver. Renders
          nothing (and starts no effects) under prefers-reduced-motion. */}
      <MarketingField />

      <div className="content">
        {/* ═════════════ NAV ═════════════ */}
        <MarketingNav
          brandVariant="pro"
          links={[
            { label: 'For golfers', to: '/' },
            { label: 'Features', href: '#features' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'Sign in', to: '/pro/login', kind: 'pill' },
          ]}
          cta={{ label: 'Start free trial', to: FREE_TRIAL_CTA }}
        />

        {/* ═════════════ HERO ═════════════ */}
        <header className="hero">
          <div className="hero-media" aria-hidden="true">
            <img src="/marketing/pro-hero.jpg" alt="" className="media-cover" />
          </div>
          <div className="wrap hero-grid">
            <div className="reveal in">
              <p className="eyebrow">Fitting to cash</p>
              <h1 className="display">
                Quote sent at 11am.<br />
                <span className="line2">Paid by lunch.</span>
              </h1>
              <p className="lede">
                Your bench knowledge is the product. GolfAuth Pro is the workflow
                that runs everything around it: the customer record, the build
                spec, the payment, the supplier order, and the handoff. One spine,
                start to finish.
              </p>
              <div className="cta-row">
                <Link to={FREE_TRIAL_CTA} className="btn big">
                  Start your 2-month free trial
                  <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
                </Link>
                <Link to="/contact?subject=pro-demo" className="btn ghost big">
                  Book a demo
                </Link>
              </div>
              <p className="reassure">
                <b>2 months free</b> · No card · Full access · Cancel anytime ·{' '}
                <Link to="/pro/login" style={{ color: 'var(--cyan)' }}>Sign in</Link>
              </p>
              <FoundingShopsBanner />
              <div className="trust">
                <div className="ti">
                  <span className="k">2 months</span>
                  <span className="l">Free trial, no card</span>
                </div>
                <div className="ti">
                  <span className="k">First in postcode</span>
                  <span className="l">Founding shops rank first</span>
                </div>
                <div className="ti">
                  <span className="k">One spine</span>
                  <span className="l">Fitting to cash</span>
                </div>
                <div className="ti">
                  <span className="k">Pro-Verified</span>
                  <span className="l">Every build carries your name</span>
                </div>
              </div>
            </div>

            {/* Beauty card: a fitting moving through the spine. */}
            <div className="stage reveal in">
              <div className="glow" aria-hidden="true" />
              <span className="floaty f1" aria-hidden="true">Paid by lunch</span>
              <span className="floaty f2" aria-hidden="true">Pro-Verified build</span>
              <div
                className="card"
                role="img"
                aria-label="A GolfAuth Pro fitting workflow: customer record, build spec, and a quote paid via Stripe Connect totalling 990 dollars"
              >
                <div className="chead">
                  <span className="clabel">Driver fitting</span>
                  <span className="ctotal">
                    <span className="ct-l">Quote total</span>
                    <br />
                    <span className="ct-v">
                      $990
                      <span style={{ fontSize: '.7rem', color: 'var(--muted)' }}> AUD</span>
                    </span>
                  </span>
                </div>
                <div className="club">
                  <div className="cn">
                    <div className="t">Customer record</div>
                    <div className="s">HCP 8 · 105mph · low-spin draw · 7yr history</div>
                  </div>
                  <span className="chip">Member</span>
                </div>
                <div className="club">
                  <div className="cn">
                    <div className="t">Build spec</div>
                    <div className="s">9° · X-Stiff · 75g · Midsize grip · +0.5° lie · +1.0"</div>
                  </div>
                  <span className="chip">Session 3</span>
                </div>
                <div className="club">
                  <div className="cn">
                    <div className="t">Quote sent</div>
                    <div className="s">Stripe Connect · sent 11:04 · viewed 11:22</div>
                  </div>
                  <span className="cv">Paid</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="wrap"><div className="rule" /></div>

        {/* ═════════════ AUDIENCE SELECTOR ═════════════
             Two jump cards: master fitter vs green-grass pro shop. Sits
             directly beneath the hero stat strip. */}
        <section className="band band-continue">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '32px' }}>
              <div>
                <p className="eyebrow">Which kind of shop are you?</p>
                <h2>Pick the one that fits.</h2>
              </div>
            </div>
            <div className="split">
              <a href="#workflow" className="apanel g reveal">
                <div className="a-bg" aria-hidden="true">
                  <img src="/marketing/panel-pro.jpg" alt="" className="media-cover" />
                </div>
                <span className="aglow" aria-hidden="true" />
                <span className="atag">Master fitters & PGA pros</span>
                <h3>I run custom builds and fittings.</h3>
                <p>
                  Customer records that survive every visit, multi-option quotes,
                  Stripe Connect checkout, and outcomes that carry your name
                  forward.
                </p>
                <span className="go">
                  See the workflow
                  <ArrowRight aria-hidden="true" />
                </span>
              </a>
              <a href="#shield" className="apanel p reveal">
                <div className="a-bg" aria-hidden="true">
                  <img src="/marketing/panel-proshop.jpg" alt="" className="media-cover" />
                </div>
                <span className="aglow" aria-hidden="true" />
                <span className="atag">Green-grass pro shops</span>
                <h3>I run shop ops at a course or club.</h3>
                <p>
                  Counterfeit trade-in verification, a lost and found audit trail,
                  and a 500-member directory that survives staff rotation. From
                  $19.99 a month — one caught fake pays for the year.
                </p>
                <span className="go">
                  See the shield
                  <ArrowRight aria-hidden="true" />
                </span>
              </a>
            </div>
            <p
              className="reveal"
              style={{
                marginTop: '20px',
                fontSize: '.9rem',
                color: 'var(--muted)',
                lineHeight: 1.6,
                maxWidth: '70ch',
              }}
            >
              Either way, your golfers get the other half of it free.{' '}
              <a href="#golfers" style={{ color: 'var(--cyan)' }}>
                See what lands in their pocket
              </a>
              .
            </p>
          </div>
        </section>

        {/* ═════════════ MANIFESTO ═════════════ */}
        <section className="band mband">
          <div className="m-bg" aria-hidden="true">
            <video
              className="media-cover"
              autoPlay
              muted
              loop
              playsInline
              poster="/marketing/manifesto2-poster.jpg"
            >
              <source src="/marketing/manifesto2.webm" type="video/webm" />
              <source src="/marketing/manifesto2.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="wrap reveal">
            <p className="eyebrow">Why GolfAuth Pro exists</p>
            <p className="manifesto">
              The bench is where you earn it.{' '}
              <span className="dim">The admin is where you lose the afternoon.</span>{' '}
              <span className="hot">We take the afternoon back.</span>
            </p>
          </div>
        </section>

        {/* ═════════════ TRUST STRIP (the trial promise) ═════════════
             Light top pad so the three caps clear the manifesto video
             band above (paddingTop: 0 sat them flush against the asset). */}
        <section
          className="band"
          style={{ paddingTop: 'clamp(40px, 5vw, 72px)' }}
        >
          <div className="wrap">
            <div className="caps reveal">
              <div className="cap">
                <span className="ci"><CreditCard aria-hidden="true" /></span>
                <div>
                  <div className="ct">2 months, no credit card</div>
                  <div className="cd">Full access, every feature, no limits. Decide on day 61.</div>
                </div>
              </div>
              <div className="cap">
                <span className="ci"><ClipboardList aria-hidden="true" /></span>
                <div>
                  <div className="ct">Run real fittings on it</div>
                  <div className="cd">Every customer record, every config, every payout: live during the trial.</div>
                </div>
              </div>
              <div className="cap">
                <span className="ci"><ShieldCheck aria-hidden="true" /></span>
                <div>
                  <div className="ct">Your data stays yours</div>
                  <div className="cd">Export the full customer base and fitting history any time, CSV or JSON.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═════════════ FEATURE ACTS (PRO_FEATURE_GROUPS) ═════════════
             The full Pro feature set, grouped into three acts that mirror
             the golfer HomePage treatment. The #workflow and #shield act
             ids feed the audience-selector jump links above. */}
        <section className="band band-continue" id="features">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '56px' }}>
              <div>
                <p className="eyebrow">Everything GolfAuth Pro does</p>
                <h2>
                  One platform for the<br />whole shop.
                </h2>
              </div>
            </div>

            <div className="acts">
              {PRO_FEATURE_GROUPS.map((group) => (
                <div
                  className="act reveal"
                  key={group.eyebrow}
                  id={group.id}
                  style={group.id ? { scrollMarginTop: '96px' } : undefined}
                >
                  <div className="ahead">
                    <p className="eyebrow">{group.eyebrow}</p>
                    <h2>{group.title}</h2>
                    {group.description && <p className="lead">{group.description}</p>}
                    {group.priceLine && (
                      <p className="lead-in">{group.priceLine}</p>
                    )}
                  </div>
                  {group.image && (
                    <div className="act-media">
                      <img
                        src={group.image}
                        alt=""
                        className="media-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="caps">
                    {group.cards.map((card) => {
                      const Icon = card.icon;
                      return (
                        <div className="cap" key={card.title}>
                          <span className="ci"><Icon aria-hidden="true" /></span>
                          <div>
                            {card.eyebrow && (
                              <div
                                className="mono"
                                style={{
                                  fontSize: '.62rem',
                                  letterSpacing: '.14em',
                                  textTransform: 'uppercase',
                                  color: 'var(--cyan)',
                                  marginBottom: '4px',
                                }}
                              >
                                {card.eyebrow}
                              </div>
                            )}
                            <div className="ct">{card.title}</div>
                            <div className="cd">{card.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {group.footnote && (
                    <p
                      className="reveal"
                      style={{
                        marginTop: '20px',
                        maxWidth: '70ch',
                        fontSize: '.84rem',
                        color: 'var(--muted)',
                        lineHeight: 1.6,
                      }}
                    >
                      {group.footnote}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="cta-row reveal" style={{ marginTop: '56px' }}>
              <Link to={FREE_TRIAL_CTA} className="btn big">
                Start your 2-month free trial
                <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
              </Link>
              <Link to="/contact?subject=pro-demo" className="btn ghost big">
                Book a demo
              </Link>
            </div>
            <p className="reassure reveal" style={{ marginTop: '16px' }}>
              <b>2 months free</b> · No card · Full access to every feature ·
              Cancel anytime
            </p>
          </div>
        </section>

        {/* ═════════════ PRICING (pro matrix) ═════════════
             PricingMatrix keeps its own internal styling (app --color-*
             tokens) and sits on the dark .mkt ground. Anchored #pricing. */}
        <section className="band band-continue">
          <div className="wrap reveal">
            <FoundingShopsBanner />
            <PricingMatrix
              id="pricing"
              eyebrow="Pricing"
              title="Pick the tier built for your shop."
              description={
                <>
                  <strong>2 months free · No card · Full access</strong> on every
                  tier. <strong>Basic</strong> is the loss-prevention play for
                  green-grass shops: counterfeit trade-in verification, L&amp;F
                  audit trail, 500-member directory. <strong>Standard</strong> is
                  where working fitters settle in: Stripe Connect, Pro Shop
                  marketplace, demo fleet tracking, 20 fittings and AI briefings
                  a month, unlimited quotes, single-supplier POs. <strong>Premium</strong> is for the
                  top of the market: multi-option shaft and grip quotes,
                  multi-supplier POs, automated quote follow-ups, equipment trend
                  insights. Pick the tier you&apos;ll run on when billing starts.
                </>
              }
              tiers={PRO_PRICING_TIERS}
              rows={PRO_PRICING_ROWS}
              footnote={
                <>
                  Every tier starts with a 2-month free trial — no credit card
                  required. Annual plans renew annually; cancel anytime. Stripe
                  Connect checkouts on Standard &amp; Premium (fitting quotes and
                  Pro Shop marketplace sales) include a GolfAuth platform fee of{' '}
                  <strong>1% of the sale price + tax on that fee</strong> (e.g. GST
                  in AU ≈ 1.1% all-in). Stripe processing fees still apply.
                  Golfer-to-golfer marketplace sales are not charged this fee.
                </>
              }
            />
          </div>
        </section>

        {/* ═════════════ FAQ (FAQ_ITEMS + openFaq) ═════════════
             Same array drives the FAQPage JSON-LD in useHeadMeta above. */}
        <section className="band band-continue" id="faq">
          <div className="wrap">
            <div className="sec-head reveal" style={{ marginBottom: '32px' }}>
              <div>
                <p className="eyebrow">Answers</p>
                <h2>Questions from fitters.</h2>
              </div>
            </div>
            <div className="reveal" style={{ maxWidth: '820px' }}>
              {FAQ_ITEMS.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={item.q}
                    style={{
                      border: '1px solid var(--hairline)',
                      borderRadius: '14px',
                      marginBottom: '10px',
                      background:
                        'linear-gradient(180deg,rgba(255,255,255,.026),rgba(255,255,255,.006))',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        padding: '18px 20px',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        color: 'var(--ink)',
                        font: 'inherit',
                        fontWeight: 700,
                        fontFamily: 'var(--display)',
                      }}
                    >
                      <span>{item.q}</span>
                      <ChevronDown
                        aria-hidden="true"
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          transition: 'transform .2s',
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          color: 'var(--cyan)',
                        }}
                      />
                    </button>
                    {isOpen && (
                      <div
                        style={{
                          padding: '0 20px 18px',
                          color: 'var(--body)',
                          lineHeight: 1.6,
                          fontSize: '.95rem',
                        }}
                      >
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═════════════ CLOSING CTA ═════════════ */}
        <section className="close hasbg">
          <div className="bandbg" aria-hidden="true">
            <img src="/marketing/cta-pro.jpg" alt="" className="media-cover" loading="lazy" />
          </div>
          <div className="wrap reveal">
            <h2>
              Put GolfAuth Pro to<br />work in your shop.
            </h2>
            <p>
              Two months free. No card. Every customer, every config, every
              payout, yours to keep when the trial ends.
            </p>
            <div className="cta-row" style={{ justifyContent: 'center' }}>
              <Link to={FINAL_CTA} className="btn big">
                Start your 2-month free trial
                <ArrowRight className="w-[18px] h-[18px]" aria-hidden="true" />
              </Link>
              <Link to="/contact?subject=pro-demo" className="btn ghost big">
                Talk to us about a demo
              </Link>
            </div>
          </div>
          <div className="wrap"><div className="rule" /></div>
        </section>

        {/* ═════════════ FOOTER ═════════════ */}
        <footer>
          <div className="wrap row">
            <span>GolfAuth Pro · The platform for fitting professionals and pro shops</span>
            <span style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
              <Link to="/">For golfers</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/delete-account">Delete account</Link>
              <Link to="/contact">Contact</Link>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
